import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useWizard } from './WizardProvider';
import StepIndicator from './components/StepIndicator';
import WizardDialog from './components/WizardDialog';
import { TOTAL_STEPS } from './types';
import { UNSAVED_TITLE, UNSAVED_BODY, UNSAVED_CONFIRM, UNSAVED_CANCEL } from './copy';

interface WizardShellProps {
  title: string;
  canAdvance: boolean;
  onExit: () => void;
  /** Step 5 has no "Next": it uses this prop to place "Complete Setup" in the same spot. */
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  children: ReactNode;
}

export default function WizardShell({ title, canAdvance, onExit, primaryAction, children }: WizardShellProps) {
  const { state, dispatch } = useWizard();
  const [confirming, setConfirming] = useState(false);
  const isFirst = state.step === 1;
  const isLast = state.step === TOTAL_STEPS;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="card p-6 lg:p-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <StepIndicator current={state.step} />
          </div>
          {!isFirst && (
            <button type="button" aria-label="Close the wizard" className="btn-icon ml-4"
              onClick={() => setConfirming(true)}>
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Step {state.step} of {TOTAL_STEPS}
        </p>

        <div className="mt-6">{children}</div>

        <div className="mt-8 flex items-center justify-between">
          <div>
            {isFirst ? (
              <button type="button" className="btn-secondary" onClick={() => setConfirming(true)}>
                Exit
              </button>
            ) : (
              <button type="button" className="btn-secondary" onClick={() => dispatch({ type: 'back' })}>
                Back
              </button>
            )}
          </div>
          {!isLast && (
            <button type="button" className="btn-primary" disabled={!canAdvance}
              onClick={() => dispatch({ type: 'next' })}>
              Next
            </button>
          )}
          {isLast && primaryAction && (
            <button type="button" className="btn-primary" disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}>
              {primaryAction.label}
            </button>
          )}
        </div>
      </div>

      <WizardDialog
        open={confirming}
        title={UNSAVED_TITLE}
        body={UNSAVED_BODY}
        confirmLabel={UNSAVED_CONFIRM}
        cancelLabel={UNSAVED_CANCEL}
        onConfirm={() => { setConfirming(false); onExit(); }}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
